import * as React from 'react';
import { useState, useMemo, useCallback, Dispatch } from 'react';
import * as R from 'ramda';
import { AddDevPlanCollaboratorModalProps, DeleteDevPlanCollaboratorModalProps, DevItemComment } from './models';
import { addDevPlanCollaboratorAction } from './api/addDevPlanCollaboratorAction';
import { useMsgText } from '../../../localization/hooks';
import { deleteDevPlanCollaboratorAction } from './api/deleteDevPlanCollaboratorAction';
import { useToggle } from '../../../core/hooks/useToggle';
import { useAppState } from '../../../core/redux/hooks';
import { useCurrentUser } from '../../../auth/hooks';
import { isCurrentUserComment, isParticipantComment } from './module';
import { useRouteMatch } from 'react-router-dom';
import { ProjectRouteParams } from '../../projectRouteParams';
import { RouteTemplates } from '../../../navigation/routes';
import { ProjIds } from '../../../navigation/hooks';
import { Str } from '../../../localization/translationKeys';


/**
 * Returns `true` if collaboration is enabled for the user's current project, otherwise false. Added here for
 * convenience since this setting is commonly checked in the dev plan components.
 */
export const useDevPlanCollaborationSetting = () => useAppState(
  state => state.projectSummary?.developmentPlanCollaborate ?? false,
);


/**
 * Returns the object identifying the current collaborator and the projects to which they're associated. Note that this
 * will be undefined if the current user is not accessing the portal as a dev plan collaborator.
 */
export const useCurrentCollaborator = () => useAppState(state => state.devPlan?.currentCollaborator);


/**
 * Returns a translated string in the event that the comment should be labeled, otherwise undefined.
 *
 * Currently this includes the case where the comment belongs to the current portal user (i.e., if it's you!).
 *
 * @param comment - the comment to check for a caption
 */
export const useDevCommenterLabel = (comment: DevItemComment) => {
  const user         = useCurrentUser();
  const collaborator = useCurrentCollaborator();

  const userEmail    = useMemo(() => user?.userName, [user]);
  const collabEmail  = useMemo(() => collaborator?.email, [collaborator]);
  const currentEmail = useMemo(() => userEmail || collabEmail || '', [collabEmail, userEmail]);

  const youCaption = useMsgText(Str.You);

  const fromCurrentUser = useMemo(() => isCurrentUserComment(comment, currentEmail), [comment, currentEmail]);
  const fromParticipant = useMemo(() => isParticipantComment(comment), [comment]);

  let response = {
    commenterLabel:    fromCurrentUser ? youCaption : comment.email,
    isFromCurrentUser: fromCurrentUser,
  };

  if (!!userEmail && fromParticipant) {
    response = {
      commenterLabel:    youCaption,
      isFromCurrentUser: true,
    };
  } else if (fromParticipant) {
    response = R.assoc(
      'commenterLabel',
      collaborator?.currentProject.participantEmail || '',
      response,
    );
  }

  return response;
};

/**
 * Returns the props necessary to enable the add-collaborator confirmation modal; clicking the 'Yes' option in this
 * modal sends an API request to send an invite to the provided email address.
 *
 * State management of the email address and comment values used for the invitation are handled by the hook.
 *
 * @param projId - the database Id for the project whose dev plan the invitee is being invited to view/collaborate in
 * @param participantId - the database Id for  the participant whose dev plan the invitee is being invited to
 * view/collaborate in
 * @param dispatch - the dispatch used to handle the invite/add action
 */
export const useAddCollaboratorModalProps = (
  projId: number,
  participantId: number,
  dispatch: Dispatch<any>,
): AddDevPlanCollaboratorModalProps => {
  // If collaboration is enabled, rather than immediately invite a collaborator when pressing the send-email button, we
  // raise a modal to inform the user of the data sharing ramifications. If they agree, we'll use the collected email
  // address and comment text to send the prospective collaborator an invite.
  const [collaboratorEmail, setCollaboratorEmail]     = useState<string | undefined>();
  const [collaboratorComment, setCollaboratorComment] = useState<string | undefined>();

  const [modalVisible, toggleModal] = useToggle();

  const { addCollaboratorModalVisible, showAddCollaboratorModal, dismissAddCollaboratorModal } = useMemo(
    () => ({
      addCollaboratorModalVisible: modalVisible,
      showAddCollaboratorModal:    (email: string, comment?: string) => {
        setCollaboratorEmail(email);
        setCollaboratorComment(comment ?? '');
        toggleModal();
      },
      dismissAddCollaboratorModal: () => {
        toggleModal();
        setCollaboratorEmail(undefined);
        setCollaboratorComment(undefined);
      },
    }),
    [modalVisible, toggleModal],
  );

  const confirmAddCollaboratorMsg = useMsgText(Str.ConfirmShare, '', { 'email': collaboratorEmail });

  const langId = useAppState(state => state.locale.lang.languageId);

  const onConfirmAddCollaborator = useCallback(
    () => {
      dispatch(
        addDevPlanCollaboratorAction(projId, participantId, collaboratorEmail ?? '', collaboratorComment ?? '', langId),
      );
      toggleModal();
    },
    [dispatch, projId, participantId, collaboratorEmail, collaboratorComment, langId, toggleModal],
  );

  return {
    confirmAddCollaboratorMsg,
    addCollaboratorModalVisible,
    showAddCollaboratorModal,
    dismissAddCollaboratorModal,
    onConfirmAddCollaborator,
  };
};


/**
 * Returns the props necessary to enable the delete-collaborator confirmation modal; clicking the 'Yes' option in this
 * modal removes access to the dev plan from the collaborator with the provided email address.
 *
 * State management of the email address provided for deletion is handled by the hook.
 *
 * @param projId - the database Id for the project whose dev plan the invitee is being removed from
 * @param participantId - the database Id for the participant whose dev plan the invitee is being removed from
 * @param dispatch - the dispatch used to handle the delete action
 */
export const useDeleteCollaboratorModalProps = (
  projId: number,
  participantId: number,
  dispatch: Dispatch<any>,
): DeleteDevPlanCollaboratorModalProps => {
  // If collaboration is enabled, rather than immediately invite a collaborator when pressing the send-email button, we
  // raise a modal to inform the user of the data sharing ramifications. If they agree, we'll use the collected email
  // address and comment text to send the prospective collaborator an invite.
  const [collaboratorEmail, setCollaboratorEmail] = useState<string | undefined>();

  const [modalVisible, toggleModal] = useToggle();

  const { deleteCollaboratorModalVisible, showDeleteCollaboratorModal, dismissDeleteCollaboratorModal } = useMemo(
    () => ({
      deleteCollaboratorModalVisible: modalVisible,
      showDeleteCollaboratorModal:    (email: string) => {
        setCollaboratorEmail(email);
        toggleModal();
      },
      dismissDeleteCollaboratorModal: () => {
        toggleModal();
        setCollaboratorEmail(undefined);
      },
    }),
    [modalVisible, toggleModal],
  );

  const confirmDeleteCollaboratorMsg = useMsgText(Str.ConfirmUnshare, '', { 'email': collaboratorEmail });

  const onConfirmDeleteCollaborator = useCallback(
    () => {
      dispatch(deleteDevPlanCollaboratorAction(projId, participantId, collaboratorEmail ?? ''));
      toggleModal();
      setCollaboratorEmail(undefined);
    },
    [collaboratorEmail, dispatch, participantId, projId, toggleModal],
  );

  return {
    confirmDeleteCollaboratorMsg,
    deleteCollaboratorModalVisible,
    showDeleteCollaboratorModal,
    dismissDeleteCollaboratorModal,
    onConfirmDeleteCollaborator,
  };
};


/**
 * A hook that returns the basic project IDs as available to collaborators:
 * * Display ID
 * * ID
 * * Participant ID
 */
export const useCollaborationProjIds = (): ProjIds => {
  const match = useRouteMatch<ProjectRouteParams>(RouteTemplates.Collaborator);

  const routePartId     = React.useMemo(() => +(match?.params.participantId ?? ''), [match]);
  const routeProjDispId = React.useMemo(() => match?.params.projectId ?? '', [match]);

  const statePartId     = useAppState(state => state.devPlan?.currentCollaborator?.currentProject.participantId);
  const stateProjDispId = useAppState(state => state.devPlan?.currentCollaborator?.currentProject.displayProjectId);
  const projId          = useAppState(state => state.devPlan?.currentCollaborator?.currentProject.projectId);

  return {
    projId:        projId || -1,
    projDisplayId: stateProjDispId || routeProjDispId || '',
    participantId: statePartId || routePartId || -1,
  };
};
